"use client"

import React from 'react';
import { motion } from 'framer-motion';
import Link from 'next/link';
import Image from 'next/image';
import { useTheme } from '../../../components/layout/ThemeProvider';
import ContactForm from '../../../components/ui/ContactForm';

const EnergiaSolarPage = () => {
  const { theme } = useTheme();
  
  // Animation variants
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6 } 
    }
  };

  // Tipos de instalações
  const tiposInstalacao = [
    {
      id: 'residencial',
      title: 'Residencial',
      description: 'Soluções para casas e condomínios',
      icon: (
        <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
          <polyline points="9 22 9 12 15 12 15 22"></polyline>
        </svg>
      ),
      beneficios: [
        'Economia de até 95% na conta de luz',
        'Valorização do imóvel',
        'Energia limpa e renovável',
        'Baixa manutenção',
        'Retorno do investimento em 4-6 anos'
      ]
    },
    {
      id: 'comercial',
      title: 'Comercial',
      description: 'Ideal para empresas e comércios',
      icon: (
        <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="4" y="2" width="16" height="20" rx="2" ry="2"></rect>
          <line x1="12" y1="18" x2="12.01" y2="18"></line>
        </svg>
      ),
      beneficios: [
        'Redução significativa de custos operacionais',
        'Marketing verde para sua marca',
        'Independência energética',
        'Incentivos fiscais',
        'Sistemas adaptados ao consumo empresarial'
      ]
    },
    {
      id: 'industrial',
      title: 'Industrial',
      description: 'Soluções de grande escala para indústrias',
      icon: (
        <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M22 12h-4l-3 9L9 3l-3 9H2"></path>
        </svg>
      ),
      beneficios: [
        'Economia significativa em altos consumos',
        'Estabilidade no custo energético',
        'Redução da pegada de carbono',
        'Sistemas robustos de alta performance',
        'Soluções customizadas para grandes demandas'
      ]
    },
    {
      id: 'rural',
      title: 'Rural',
      description: 'Energia para fazendas e propriedades rurais',
      icon: (
        <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 3v18"></path>
          <rect x="4" y="8" width="16" height="12" rx="2"></rect>
          <path d="M2 20h20"></path>
        </svg>
      ),
      beneficios: [
        'Independência da rede em áreas remotas',
        'Irrigação com energia renovável',
        'Economia em propriedades com alto consumo',
        'Sistemas híbridos disponíveis',
        'Ideal para bombeamento de água solar'
      ]
    }
  ];

  // Benefícios da energia solar
  const beneficiosPrincipais = [
    {
      titulo: 'Economia Financeira',
      descricao: 'Reduza sua conta de luz em até 95%, garantindo economia mensal significativa',
      icone: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
        </svg>
      )
    },
    {
      titulo: 'Energia Sustentável',
      descricao: 'Utilize uma fonte de energia limpa e renovável, reduzindo sua pegada de carbono',
      icone: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8h1a4 4 0 0 1 0 8h-1"></path>
          <path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z"></path>
          <line x1="6" y1="1" x2="6" y2="4"></line>
          <line x1="10" y1="1" x2="10" y2="4"></line>
          <line x1="14" y1="1" x2="14" y2="4"></line>
        </svg>
      )
    },
    {
      titulo: 'Valorização Imobiliária',
      descricao: 'Aumente o valor do seu imóvel com um sistema moderno e econômico de geração de energia',
      icone: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
          <polyline points="9 22 9 12 15 12 15 22"></polyline>
        </svg>
      )
    },
    {
      titulo: 'Baixa Manutenção',
      descricao: 'Sistemas fotovoltaicos requerem mínima manutenção e têm longa vida útil (25+ anos)',
      icone: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"></circle>
          <polyline points="12 6 12 12 16 14"></polyline>
        </svg>
      )
    },
    {
      titulo: 'Independência Energética',
      descricao: 'Reduza sua dependência das concessionárias e dos aumentos constantes de tarifas',
      icone: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
        </svg>
      )
    },
    {
      titulo: 'Retorno do Investimento',
      descricao: 'Recupere seu investimento inicial em um período médio de 4 a 6 anos',
      icone: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
        </svg>
      ),
      destaque: true
    },
    {
      titulo: 'Adaptabilidade',
      descricao: 'Sistemas modulares que podem ser expandidos conforme a necessidade do cliente',
      icone: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
          <line x1="9" y1="9" x2="15" y2="15"></line>
          <line x1="15" y1="9" x2="9" y2="15"></line>
        </svg>
      )
    },
    {
      titulo: 'Garantia Estendida',
      descricao: 'Equipamentos com garantia de até 25 anos e monitoramento constante de desempenho',
      icone: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
        </svg>
      )
    }
  ];

  // Etapas do processo
  const etapasProcesso = [
    {
      numero: '01',
      titulo: 'Avaliação Inicial',
      descricao: 'Análise da viabilidade técnica e levantamento do consumo energético atual.'
    },
    {
      numero: '02',
      titulo: 'Projeto Personalizado',
      descricao: 'Desenvolvimento de projeto específico para suas necessidades e tipo de instalação.'
    },
    {
      numero: '03',
      titulo: 'Proposta Comercial',
      descricao: 'Apresentação da solução com detalhamento técnico, benefícios e investimento.'
    },
    {
      numero: '04',
      titulo: 'Aprovação e Documentação',
      descricao: 'Cuidamos de toda a documentação junto à concessionária de energia.'
    },
    {
      numero: '05',
      titulo: 'Instalação do Sistema',
      descricao: 'Instalação completa por equipe técnica certificada e especializada.'
    },
    {
      numero: '06',
      titulo: 'Vistoria e Homologação',
      descricao: 'Acompanhamento da vistoria da concessionária e homologação do sistema.'
    },
    {
      numero: '07',
      titulo: 'Ativação e Monitoramento',
      descricao: 'Ativação do sistema e configuração do monitoramento remoto de desempenho.'
    }
  ];

  // FAQ items
  const faqItems = [
    {
      pergunta: 'Quanto tempo leva para instalar um sistema de energia solar?',
      resposta: 'O tempo de instalação varia conforme o tamanho e a complexidade do sistema. Para residências, a instalação geralmente leva de 2 a 3 dias. Para sistemas comerciais ou industriais de maior porte, o processo pode levar de 1 a 3 semanas. Este prazo considera apenas a instalação física dos equipamentos. O processo completo, incluindo projeto, aprovação da concessionária e homologação, pode levar de 45 a 90 dias. Nossa equipe trabalha para agilizar todas as etapas, mantendo você informado durante todo o processo.'
    },
    {
      pergunta: 'Qual a economia real que posso ter com energia solar?',
      resposta: 'A economia com energia solar pode chegar a até 95% na conta de luz. Em média, nossos clientes obtêm redução de 85% a 90% em suas faturas mensais. O valor exato da economia depende de diversos fatores como: seu consumo atual, tarifa da concessionária local, condições de irradiação solar da região, tipo de telhado e sombreamento. Durante nossa avaliação inicial, realizamos um estudo detalhado para projetar sua economia real, considerando inclusive as bandeiras tarifárias e reajustes futuros. Com o sistema dimensionado corretamente, você pagará apenas a taxa mínima de disponibilidade da rede, que varia conforme o tipo de conexão (monofásica, bifásica ou trifásica).'
    },
    {
      pergunta: 'Os painéis solares funcionam em dias nublados ou chuvosos?',
      resposta: 'Sim, os painéis solares continuam gerando energia mesmo em dias nublados ou chuvosos, porém com eficiência reduzida. Em dias com nebulosidade parcial, a produção pode chegar a 70-80% da capacidade. Em dias muito nublados ou com chuva intensa, a produção pode cair para 10-30% da capacidade nominal. É importante ressaltar que o sistema é dimensionado considerando essas variações climáticas ao longo do ano. Utilizamos dados históricos de irradiação solar da sua região para calcular a média anual de geração, garantindo que mesmo com dias menos produtivos, o balanço anual atenda às suas necessidades energéticas. Além disso, o sistema de compensação de energia da ANEEL permite acumular créditos nos meses mais ensolarados para utilizar nos meses com menor geração.'
    },
    {
      pergunta: 'Qual é o tempo de retorno do investimento (payback) de um sistema fotovoltaico?',
      resposta: 'O tempo médio de retorno do investimento (payback) para sistemas fotovoltaicos no Brasil atualmente varia entre 4 a 6 anos, dependendo de diversos fatores. Para residências, o payback médio é de aproximadamente 5 anos. Para empresas e indústrias, que geralmente têm tarifas energéticas mais altas e consumo concentrado durante o dia, o retorno pode ser ainda mais rápido, em torno de 3 a 5 anos. Fatores que influenciam o payback incluem: valor da tarifa de energia local, eficiência do sistema instalado, condições de irradiação solar da região, qualidade dos equipamentos e eventuais aumentos na tarifa de energia durante a vida útil do sistema. Considerando que os sistemas fotovoltaicos têm vida útil superior a 25 anos, após o período de payback você desfrutará de economia por muitos anos.'
    },
    {
      pergunta: 'Preciso de manutenção frequente nos painéis solares?',
      resposta: 'Os sistemas fotovoltaicos requerem manutenção mínima, o que é uma de suas grandes vantagens. Recomendamos uma limpeza básica dos painéis de 6 em 6 meses para remover poeira, folhas ou outros detritos que possam reduzir a eficiência. Em regiões com maior poluição ou próximas ao litoral, a frequência pode ser um pouco maior. Além da limpeza, é importante realizar uma inspeção técnica anual para verificar as conexões elétricas, inversores e estrutura de fixação. Nossos sistemas incluem monitoramento remoto que permite identificar qualquer queda de desempenho, facilitando a manutenção preventiva. Oferecemos planos de manutenção periódica que garantem o máximo desempenho do seu sistema ao longo de toda sua vida útil, que pode ultrapassar 25 anos com os cuidados adequados.'
    },
    {
      pergunta: 'É possível instalar energia solar em apartamentos?',
      resposta: 'Sim, é possível que moradores de apartamentos aproveitem os benefícios da energia solar através de algumas alternativas. A principal delas é a instalação em áreas comuns do condomínio, como telhados ou coberturas de garagens, mediante aprovação em assembleia. Outra opção é através da modalidade de geração compartilhada, onde você pode participar de consórcios ou cooperativas de energia solar, ou ainda investir em fazendas solares. Uma terceira alternativa é a instalação em outra propriedade que esteja no mesmo CPF/CNPJ e área de concessão da distribuidora (geração remota). Durante nossa consultoria, analisamos qual a melhor solução para o seu caso específico, considerando aspectos técnicos, financeiros e as regras do condomínio, garantindo que mesmo morando em apartamento, você possa reduzir sua conta de luz com energia limpa e renovável.'
    }
  ];

  // Calculadora de economia solar
  const calculadoraSolar = {
    instrucoes: [
      'Verifique o valor médio de sua conta de luz mensal',
      'Considere o consumo em kWh (não o valor em reais)',
      'Analise a incidência solar no seu telhado',
      'Calcule o espaço disponível para a instalação',
      'Considere seu tipo de conexão (mono, bi ou trifásica)'
    ],
    resultados: [
      '85-95% de economia na conta de luz',
      'Retorno do investimento em 4-6 anos',
      'Valorização do imóvel em até 10%',
      'Redução de emissão de CO2',
      'Garantia de 25 anos nos painéis solares'
    ]
  };

  // Benefícios da parceria
  const nossosDestaques = [
    {
      titulo: 'Parceria Estratégica',
      descricao: 'Web Imports e SunRight unidas para oferecer o melhor em energia solar',
      icone: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5"></path>
          <path d="M12 14v7"></path>
          <path d="M8 9v.5"></path>
          <path d="M16 9v.5"></path>
          <path d="M12 2v4"></path>
        </svg>
      )
    },
    {
      titulo: 'Mais de 10 Anos de Experiência',
      descricao: 'SunRight atua no mercado desde 2014 com excelência comprovada',
      icone: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
        </svg>
      )
    },
    {
      titulo: 'Economia Garantida',
      descricao: 'Garantimos redução significativa na sua conta de energia',
      icone: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>
      )
    },
    {
      titulo: 'Equipamentos Premium',
      descricao: 'Utilizamos apenas componentes de alta qualidade e eficiência',
      icone: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
        </svg>
      )
    },
    {
      titulo: 'Projeto Personalizado',
      descricao: 'Cada sistema é projetado especificamente para suas necessidades',
      icone: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
        </svg>
      )
    },
    {
      titulo: 'Suporte Contínuo',
      descricao: 'Acompanhamento e monitoramento após a instalação',
      icone: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
        </svg>
      ),
      observacao: "* Atendimento In Loco incide taxa de visita técnica a ser previamente combinada"
    }
  ];

  return (
    <>
      {/* Breadcrumbs */}
      <div className={`${theme === 'dark' ? 'bg-gray-800' : 'bg-gray-100'} py-2`}>
        <div className="container mx-auto px-4">
          <nav className="flex" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-2">
              <li>
                <Link 
                  href="/" 
                  className={`${theme === 'dark' ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
                >
                  Início
                </Link>
              </li>
              <li className={`${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
              </li>
              <li>
                <Link 
                  href="/servicos" 
                  className={`${theme === 'dark' ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
                >
                  Serviços
                </Link>
              </li>
              <li className={`${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
              </li>
              <li>
                <span className={`${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'} font-medium`}>
                  Energia Solar
                </span>
              </li>
            </ol>
          </nav>
        </div>
      </div>

      {/* Hero Section */}
      <section 
        className={`py-10 ${theme === 'dark' ? 'bg-gray-900' : 'bg-white'} relative overflow-hidden`}
      >
        {/* Background decorativo */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <svg 
            className="absolute right-0 top-0 transform translate-x-1/3 -translate-y-1/4 opacity-20" 
            width="800" 
            height="800" 
            fill="none" 
            viewBox="0 0 800 800"
            aria-hidden="true"
          >
            <circle 
              cx="400" 
              cy="400" 
              r="400" 
              fill={theme === 'dark' ? "#F59E0B" : "#FCD34D"} 
              fillOpacity="0.2" 
            />
          </svg>
          
          <svg 
            className="absolute left-0 bottom-0 transform -translate-x-1/4 translate-y-1/4 opacity-20" 
            width="800" 
            height="800" 
            fill="none" 
            viewBox="0 0 800 800"
            aria-hidden="true"
          >
            <circle 
              cx="400" 
              cy="400" 
              r="400" 
              fill={theme === 'dark' ? "#EC4899" : "#FB7185"} 
              fillOpacity="0.2" 
            />
          </svg>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="flex flex-col lg:flex-row items-center gap-8">
            <motion.div 
              initial="hidden" 
              animate="visible" 
              variants={fadeIn}
              className="lg:w-1/2"
            >
              <div className={`inline-block px-3 py-1 text-sm font-semibold ${
                theme === 'dark' 
                  ? 'text-amber-300 bg-amber-900/50' 
                  : 'text-amber-800 bg-amber-100'
                } rounded-full mb-3`}>
                Energia Limpa e Renovável
              </div>
              <h1 className={`text-3xl lg:text-4xl font-bold leading-tight ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              } mb-4`}>
                Energia <span className="text-amber-500">Solar</span> Fotovoltaica
              </h1>
              <p className={`text-lg ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
              } mb-6`}>
                Reduza sua conta de luz em até 95% com soluções completas de energia solar 
                para residências, comércios e indústrias.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <motion.a 
                  href="#contato" 
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-8 py-3 bg-amber-500 hover:bg-amber-600 text-white font-extrabold text-lg tracking-wide rounded-lg shadow-lg hover:shadow-xl transition-all text-center"
                  style={{
                    textShadow: '0 1px 2px rgba(0,0,0,0.2)',
                    color: '#ffffff'
                  }}
                >
                  Solicitar Orçamento
                </motion.a>
                <motion.a 
                  href="#beneficios" 
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className={`px-8 py-3 ${
                    theme === 'dark' 
                      ? 'bg-gray-800 hover:bg-gray-700 text-white' 
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-800'
                  } font-extrabold text-lg tracking-wide rounded-lg shadow hover:shadow-lg transition-all text-center`}
                >
                  Ver Benefícios
                </motion.a>
              </div>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="lg:w-1/2"
            >
              <div className="relative">
                <div className={`w-full h-80 rounded-lg ${
                  theme === 'dark' ? 'bg-gray-800' : 'bg-amber-50'
                } flex items-center justify-center overflow-hidden`}>
                  {/* Imagem Hero otimizada */}
                  <Image
                    src="/images/energia-solar-illustration.svg"
                    alt="Energia Solar Fotovoltaica"
                    width={500}
                    height={400}
                    className="object-contain max-w-full max-h-full"
                  />
                </div>
                
                {/* Estatística flutuante */}
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8, duration: 0.5 }}
                  className={`absolute -bottom-6 -right-6 p-6 rounded-lg shadow-xl ${
                    theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white'
                  }`}
                >
                  <p className="text-amber-500 font-bold text-4xl">95%</p>
                  <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} text-sm`}>
                    de economia na conta de luz
                  </p>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Tipos de Instalação Section */}
      <section 
        id="tipos-instalacao" 
        className={`py-10 ${theme === 'dark' ? 'bg-gray-800' : 'bg-gray-50'}`}
      >
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-8">
            <div className={`inline-block px-3 py-1 text-sm font-semibold ${
              theme === 'dark' 
                ? 'text-amber-300 bg-amber-900/50' 
                : 'text-amber-800 bg-amber-100'
              } rounded-full mb-3`}>
              Soluções Completas
            </div>
            <h2 className={`text-2xl md:text-3xl font-bold ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            } mb-3`}>
              Tipos de Instalação Solar
            </h2>
            <p className={`text-lg ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
            }`}>
              Oferecemos soluções personalizadas para cada necessidade, com projetos sob medida
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {tiposInstalacao.map((tipo) => (
              <motion.div
                key={tipo.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5 }}
                className={`${
                  theme === 'dark' ? 'bg-gray-900' : 'bg-white'
                } rounded-xl p-4 shadow-lg h-full`}
              >
                <div className={`w-12 h-12 mb-4 rounded-lg ${
                  theme === 'dark' 
                    ? 'bg-gradient-to-br from-amber-900 to-amber-800 text-amber-300' 
                    : 'bg-gradient-to-br from-amber-500 to-amber-600 text-white'
                  } flex items-center justify-center`}>
                  {tipo.icon}
                </div>
                
                <h3 className={`text-xl font-bold ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                } mb-2`}>{tipo.title}</h3>
                
                <p className={`mb-4 ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                }`}>{tipo.description}</p>
                
                <ul className="space-y-2">
                  {tipo.beneficios.map((beneficio, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <svg className={`w-5 h-5 ${
                        theme === 'dark' ? 'text-amber-500' : 'text-amber-600'
                      } mt-0.5 flex-shrink-0`} fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className={`${
                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                      } text-sm`}>{beneficio}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefícios Section */}
      <section 
        id="beneficios" 
        className={`py-10 ${theme === 'dark' ? 'bg-gray-900' : 'bg-white'}`}
      >
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-8">
            <div className={`inline-block px-3 py-1 text-sm font-semibold ${
              theme === 'dark' 
                ? 'text-amber-300 bg-amber-900/50' 
                : 'text-amber-800 bg-amber-100'
              } rounded-full mb-3`}>
              Vantagens da Energia Solar
            </div>
            <h2 className={`text-2xl md:text-3xl font-bold ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            } mb-3`}>
              Benefícios de Investir em Energia Solar
            </h2>
            <p className={`text-lg ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
            }`}>
              Descubra por que milhares de pessoas estão optando por energia limpa e renovável
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {beneficiosPrincipais.map((beneficio, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={`${
                  beneficio.destaque 
                    ? theme === 'dark' 
                      ? 'bg-amber-900/40 border border-amber-800' 
                      : 'bg-amber-50 border border-amber-200'
                    : theme === 'dark' 
                      ? 'bg-gray-800' 
                      : 'bg-white'
                } rounded-xl p-4 shadow-lg h-full`}
              >
                <div className={`w-10 h-10 rounded-full ${
                  theme === 'dark' 
                    ? beneficio.destaque ? 'bg-amber-800 text-amber-200' : 'bg-gray-700 text-gray-300'
                    : beneficio.destaque ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-800'
                  } flex items-center justify-center mb-4`}>
                  {beneficio.icone}
                </div>
                
                <h3 className={`text-lg font-bold ${
                  theme === 'dark' 
                    ? beneficio.destaque ? 'text-amber-200' : 'text-white'
                    : beneficio.destaque ? 'text-amber-800' : 'text-gray-900'
                } mb-2`}>{beneficio.titulo}</h3>
                
                <p className={`text-sm ${
                  theme === 'dark' 
                    ? beneficio.destaque ? 'text-amber-100/90' : 'text-gray-400'
                    : beneficio.destaque ? 'text-amber-900/90' : 'text-gray-600'
                }`}>{beneficio.descricao}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Processo Section */}
      <section 
        id="processo" 
        className={`py-10 ${theme === 'dark' ? 'bg-gray-800' : 'bg-gray-50'}`}
      >
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-8">
            <div className={`inline-block px-3 py-1 text-sm font-semibold ${
              theme === 'dark' 
                ? 'text-amber-300 bg-amber-900/50' 
                : 'text-amber-800 bg-amber-100'
              } rounded-full mb-3`}>
              Metodologia
            </div>
            <h2 className={`text-2xl md:text-3xl font-bold ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            } mb-3`}>
              Como Funciona Nossa Instalação
            </h2>
            <p className={`text-lg ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
            }`}>
              Um processo completo e transparente, do projeto à ativação do seu sistema
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {etapasProcesso.map((etapa, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={`${
                  theme === 'dark' ? 'bg-gray-900' : 'bg-white'
                } rounded-xl p-4 shadow-lg h-full relative overflow-hidden`}
              >
                <div className={`flex items-center mb-3`}>
                  <div className={`w-8 h-8 rounded-lg ${
                    theme === 'dark' 
                      ? 'bg-gradient-to-br from-amber-900 to-amber-800 text-amber-300' 
                      : 'bg-gradient-to-br from-amber-500 to-amber-600 text-white'
                    } flex items-center justify-center mr-2 font-medium`}>
                    {index + 1}
                  </div>
                  <h3 className={`text-lg font-bold ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}>{etapa.titulo}</h3>
                </div>
                <p className={`${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                } pl-10 text-sm`}>
                  {etapa.descricao}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Calculator Section */}
      <section 
        id="calculadora" 
        className={`py-10 ${theme === 'dark' ? 'bg-gray-900' : 'bg-white'}`}
      >
        <div className="container mx-auto px-4">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className={`relative p-8 rounded-xl mb-8 ${
              theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white shadow-xl'
            }`}
          >
            <div className="text-center mb-8">
              <h3 className={`text-2xl font-bold ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>Calculadora de Economia Solar</h3>
              <p className={`mt-2 ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
              }`}>
                Estime o potencial de economia com energia solar para seu caso específico
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div className={`p-6 rounded-lg ${
                theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'
              }`}>
                <h4 className={`text-xl font-bold mb-4 ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>Para calcular sua economia</h4>
                <ul className="space-y-3">
                  {calculadoraSolar.instrucoes.map((instrucao, i) => (
                    <li key={i} className="flex items-start">
                      <span className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center mr-2 ${
                        theme === 'dark' ? 'bg-amber-900 text-amber-200' : 'bg-amber-100 text-amber-800'
                      } text-sm font-bold`}>{i+1}</span>
                      <span className={`${
                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                      }`}>{instrucao}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className={`p-6 rounded-lg ${
                theme === 'dark' ? 'bg-amber-900/30' : 'bg-amber-50'
              }`}>
                <h4 className={`text-xl font-bold mb-4 ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>Resultados esperados</h4>
                <ul className="space-y-3">
                  {calculadoraSolar.resultados.map((resultado, i) => (
                    <li key={i} className="flex items-start">
                      <svg className={`w-5 h-5 mr-2 mt-0.5 ${
                        theme === 'dark' ? 'text-amber-400' : 'text-amber-600'
                      }`} fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className={`${
                        theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
                      }`}>{resultado}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            
            <div className="mt-8 p-4 rounded-lg bg-amber-500/10 border border-amber-500/20">
              <div className="flex items-start">
                <svg className="w-6 h-6 mr-2 text-amber-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                <p className={`${
                  theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
                }`}>
                  <span className="font-medium">IMPORTANTE: </span> 
                  Para uma estimativa precisa de economia e dimensionamento do sistema ideal para você, 
                  agende uma consulta gratuita. Nossos especialistas farão uma análise personalizada 
                  considerando seu consumo real, localização e tipo de telhado.
                </p>
              </div>
            </div>
            
            <div className="mt-8 text-center">
              <motion.a 
                href="#contato" 
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-8 py-3 bg-amber-500 hover:bg-amber-600 text-white font-extrabold text-lg tracking-wide rounded-lg shadow-lg hover:shadow-xl transition-all inline-flex items-center"
              >
                Solicitar análise personalizada
                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </motion.a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Destaques Section */}
      <section 
        id="destaques" 
        className={`py-10 ${theme === 'dark' ? 'bg-gray-800' : 'bg-gray-50'}`}
      >
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-8">
            <div className={`inline-block px-3 py-1 text-sm font-semibold ${
              theme === 'dark' 
                ? 'text-amber-300 bg-amber-900/50' 
                : 'text-amber-800 bg-amber-100'
              } rounded-full mb-3`}>
              Nossos Diferenciais
            </div>
            <h2 className={`text-2xl md:text-3xl font-bold ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            } mb-3`}>
              Por que Escolher Nossos Serviços
            </h2>
            <p className={`text-lg ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
            }`}>
              Compromisso com qualidade e excelência em todas as etapas do projeto
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {nossosDestaques.map((destaque, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={`${
                  theme === 'dark' ? 'bg-gray-900' : 'bg-white'
                } rounded-xl p-4 shadow-lg`}
              >
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-lg ${
                    theme === 'dark' ? 'bg-amber-900/40 text-amber-300' : 'bg-amber-100 text-amber-800'
                  }`}>
                    {destaque.icone}
                  </div>
                  <div>
                    <h3 className={`text-lg font-bold ${
                      theme === 'dark' ? 'text-white' : 'text-gray-900'
                    } mb-1`}>{destaque.titulo}</h3>
                    <p className={`${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    } text-sm`}>{destaque.descricao}</p>
                    
                    {destaque.observacao && (
                      <p className={`mt-2 text-xs italic ${
                        theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
                      }`}>{destaque.observacao}</p>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section 
        id="faq"
        className={`py-10 ${theme === 'dark' ? 'bg-gray-900' : 'bg-white'}`}
      >
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-8">
            <div className={`inline-block px-3 py-1 text-sm font-semibold ${
              theme === 'dark' 
                ? 'text-amber-300 bg-amber-900/50' 
                : 'text-amber-800 bg-amber-100'
              } rounded-full mb-3`}>
              Dúvidas Frequentes
            </div>
            <h2 className={`text-2xl md:text-3xl font-bold ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            } mb-3`}>
              Perguntas Frequentes sobre Energia Solar
            </h2>
            <p className={`text-lg ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
            }`}>
              Esclarecemos suas principais dúvidas sobre sistemas fotovoltaicos
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            {faqItems.map((item, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className={`mb-4 rounded-lg ${
                  theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white shadow'
                } overflow-hidden`}
              >
                <details className="group">
                  <summary className={`flex items-center justify-between p-4 cursor-pointer ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}>
                    <h3 className="text-lg font-semibold">{item.pergunta}</h3>
                    <span className="relative ml-2 flex-shrink-0">
                      <svg 
                        className={`w-5 h-5 transition-transform transform ${
                          theme === 'dark' ? 'text-amber-300' : 'text-amber-600'
                        } group-open:rotate-180`} 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                      </svg>
                    </span>
                  </summary>
                  <div className={`px-4 pb-4 pt-0 ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    <p className="text-sm">{item.resposta}</p>
                  </div>
                </details>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        id="contato"
        className={`py-10 ${theme === 'dark' ? 'bg-gray-800' : 'bg-gray-50'} relative overflow-hidden`}
      >
        {/* Background decorativo */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <svg 
            className="absolute top-0 left-0 transform -translate-x-1/2 -translate-y-1/2" 
            width="600" 
            height="600" 
            fill="none" 
            viewBox="0 0 600 600"
            aria-hidden="true"
          >
            <circle 
              cx="300" 
              cy="300" 
              r="300" 
              fill={theme === 'dark' ? "#78350F" : "#FCD34D"} 
              fillOpacity="0.05" 
            />
          </svg>
          
          <svg 
            className="absolute bottom-0 right-0 transform translate-x-1/2 translate-y-1/2" 
            width="600" 
            height="600" 
            fill="none" 
            viewBox="0 0 600 600"
            aria-hidden="true"
          >
            <circle 
              cx="300" 
              cy="300" 
              r="300" 
              fill={theme === 'dark' ? "#92400E" : "#F59E0B"} 
              fillOpacity="0.05" 
            />
          </svg>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5 }}
            >
              <div className={`inline-block px-3 py-1 text-sm font-semibold ${
                theme === 'dark' 
                  ? 'text-amber-300 bg-amber-900/50' 
                  : 'text-amber-800 bg-amber-100'
                } rounded-full mb-3`}>
                Fale Conosco
              </div>
              <h2 className={`text-2xl md:text-3xl font-bold ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              } mb-3`}>
                Solicite um Orçamento Gratuito
              </h2>
              <p className={`text-lg ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
              } mb-6`}>
                Nossa equipe está pronta para desenvolver o sistema ideal para sua necessidade,
                com projeto personalizado e condições especiais.
              </p>
              
              <div className="space-y-4">
                <div className={`flex items-center ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  <div className={`w-12 h-12 rounded-full ${
                    theme === 'dark' ? 'bg-amber-900/40' : 'bg-amber-100'
                  } flex items-center justify-center mr-4 ${
                    theme === 'dark' ? 'text-amber-300' : 'text-amber-700'
                  }`}>
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium">Telefone</p>
                    <a 
                      href="tel:+551932060706" 
                      className={`${
                        theme === 'dark' ? 'text-amber-300 hover:text-amber-200' : 'text-amber-600 hover:text-amber-800'
                      }`}
                    >
                      (19) 3206-0706
                    </a>
                  </div>
                </div>
                
                <div className={`flex items-center ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  <div className={`w-12 h-12 rounded-full ${
                    theme === 'dark' ? 'bg-green-900/40' : 'bg-green-100'
                  } flex items-center justify-center mr-4 ${
                    theme === 'dark' ? 'text-green-300' : 'text-green-700'
                  }`}>
                    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium">WhatsApp</p>
                    <a 
                      href="https://wa.me/5519994799400" 
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`${
                        theme === 'dark' ? 'text-green-300 hover:text-green-200' : 'text-green-600 hover:text-green-800'
                      }`}
                    >
                      (19) 99479-9400
                    </a>
                  </div>
                </div>
                
                <div className={`flex items-center ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  <div className={`w-12 h-12 rounded-full ${
                    theme === 'dark' ? 'bg-amber-900/40' : 'bg-amber-100'
                  } flex items-center justify-center mr-4 ${
                    theme === 'dark' ? 'text-amber-300' : 'text-amber-700'
                  }`}>
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium">Email</p>
                    <a 
                      href="mailto:contato@webimports.com.br" 
                      className={`${
                        theme === 'dark' ? 'text-amber-300 hover:text-amber-200' : 'text-amber-600 hover:text-amber-800'
                      }`}
                    >
                      contato@webimports.com.br
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5 }}
              className={`${
                theme === 'dark' ? 'bg-gray-900 border border-gray-700' : 'bg-white shadow-xl'
              } rounded-xl p-5`}
            >
              <h3 className={`text-xl font-bold ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              } mb-4`}>
                Economize na sua conta de luz
              </h3>
              
              <ContactForm />
              
              <p className={`mt-4 text-xs ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`}>
                Ao enviar este formulário, você concorda com nossa política de privacidade.
                Seus dados não serão compartilhados com terceiros.
              </p>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
};

export default EnergiaSolarPage;